import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

import { TagIdAndPercentage, TriageAction, TriageItem } from '../shared/models';

/**
 * Used as downgraded in AJS.
 * This component is child of child of a parent form which is in AjS (triage-rule-edition.dialog.html).
 * Problem : the AJS form can't see controls with ngModel in Angular (validity and errors).
 * So we are building a reactive form to manage controls here,
 * and put the validity of the form in action object, to manage the state of the save button in the parent component.
 */
@Component({
  selector: 'app-random-dispatch-action',
  templateUrl: './random-dispatch-action.component.html',
  styleUrls: ['./random-dispatch-action.component.scss'],
})
export class RandomDispatchActionComponent implements OnInit, OnDestroy {
  @Input() action: TriageAction;
  @Input() items: TriageItem[];
  @Output() formChange = new EventEmitter();

  private subscriptions = new Subscription();

  selectedGroup = null;
  percentageItems = [];
  isTypeRandomDispatch = false;
  totalPercentageForError: number;

  tagIdsAndPercentages: TagIdAndPercentage[];

  readonly newTagIdAndPercentage: TagIdAndPercentage = {
    tagId: null,
    percentage: null,
  };

  form = this.formBuilder.group({
    tagGroupId: [null, Validators.required],
    tagIdsAndPercentagesFormArray: this.formBuilder.array([]),
  });

  get tagIdsAndPercentagesFormArray() {
    return this.form.get('tagIdsAndPercentagesFormArray') as FormArray;
  }

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    for (let p = 0; p <= 100; p += 5) {
      this.percentageItems.push({
        id: p,
        label: `${p}%`,
      });
    }

    this.isTypeRandomDispatch = this.action.isTypeRandomDispatch;

    // without this copy (not even deep), "formArray.clear()" delete the content of "this.action.parameters.tagIdsAndPercentages" (!?) and it's a real mess
    this.tagIdsAndPercentages = this.action.parameters.tagIdsAndPercentages;

    this.form.get('tagGroupId').setValue(this.action.parameters.tagGroupId);
    this.initTagIdsAndPercentagesFormArray();
    this.updateSelelectedGroup();

    // add validator after all inits, to avoid false positive errors
    if (this.isTypeRandomDispatch) {
      this.form.addValidators(this.percentagesSumValidator());
    }

    this.syncFormValidity();

    this.subscriptions.add(
      this.form.valueChanges.subscribe(() => {
        const { tagGroupId, tagIdsAndPercentagesFormArray } = this.form.value;
        this.action.parameters = { tagGroupId, tagIdsAndPercentages: tagIdsAndPercentagesFormArray };
        this.syncFormValidity();
      })
    );
  }

  /**
   * Put the form validity in "action" object, wich is visible by the parent component.
   * And emit an EventEmitter, so the parent can read the object to manage the save button activation (enabled/disabled)
   */
  private syncFormValidity() {
    this.action.isFormValid = this.form.valid;
    this.formChange.emit();
  }

  private initTagIdsAndPercentagesFormArray() {
    this.tagIdsAndPercentagesFormArray.clear();
    this.tagIdsAndPercentages.forEach(tag => {
      this.addTagIdsAndPercentagesFormArray(tag.tagId, tag.percentage);
    });
  }

  /**
   * Add a new FormGroup of controls in FormArray
   */
  private addTagIdsAndPercentagesFormArray(tagId: number, percentage: number) {
    const tagIdsAndPercentagesForm = this.formBuilder.group({
      tagId: [tagId, Validators.required],
      percentage: [percentage, Validators.required],
    });
    this.tagIdsAndPercentagesFormArray.push(tagIdsAndPercentagesForm);
  }

  /**
   * Group = items.
   * It's either a tag, "Language" or "Team", but the label in GUI is "Tag"
   */
  onGroupChange() {
    this.updateSelelectedGroup();
    this.resetTagIdsAndPercentages();
  }

  /**
   * Update the group and all sub items, so all the selects are updated
   */
  private updateSelelectedGroup() {
    this.selectedGroup = this.items.find(group => group.id === this.form.value.tagGroupId);
  }

  private resetTagIdsAndPercentages() {
    this.tagIdsAndPercentages = [{ ...this.newTagIdAndPercentage }, { ...this.newTagIdAndPercentage }];
    this.initTagIdsAndPercentagesFormArray();
  }

  addSubItem() {
    this.addTagIdsAndPercentagesFormArray(null, null);
  }

  removeSubItem(subItemIndex: number) {
    this.tagIdsAndPercentagesFormArray.removeAt(subItemIndex);
  }

  onPercentageChange(changedIndex: number) {
    if (!this.isTypeRandomDispatch) {
      this.synchronizePercentages(changedIndex);
    }
  }

  /**
   * Adjusts other percentages when the user changes one dropdown, so that the total is 100
   * @remarks Only works with two tag values
   */
  private synchronizePercentages(changedIndex: number) {
    const indexToSynchronize = 1 - changedIndex;
    const changedPercentage = this.tagIdsAndPercentagesFormArray.controls[changedIndex].value.percentage;

    (this.tagIdsAndPercentagesFormArray.controls[indexToSynchronize] as FormGroup)
      .get('percentage')
      .setValue(100 - changedPercentage);
  }

  /**
   * Validate if the sum of all percentages is equal to 100, and display errors if not.
   * @remarks Do not trigger error if all percentages are not filled.
   */
  private percentagesSumValidator(): ValidatorFn {
    return (form: FormGroup): ValidationErrors | null => {
      const removeErrorOnSelects = () => {
        tagIdsAndPercentagesFormArray.controls.forEach((group: FormGroup) => {
          if (group.get('percentage').hasError('percentagesSum')) {
            group.get('percentage').setErrors(null);
          }
        });
      };

      const tagIdsAndPercentagesFormArray = form.get('tagIdsAndPercentagesFormArray') as FormArray;

      if (!tagIdsAndPercentagesFormArray.value.length) {
        return null;
      }

      let total = 0;
      let isEveryPercentageNotNull = false;

      // sum the total
      isEveryPercentageNotNull = tagIdsAndPercentagesFormArray.value.every((tagIdAndPercentage: TagIdAndPercentage) => {
        if (tagIdAndPercentage.percentage !== null) {
          total += tagIdAndPercentage.percentage;
          return true;
        }
        return false;
      });

      if (!isEveryPercentageNotNull) {
        removeErrorOnSelects(); // in case of adding a new field when an error exists
        return null;
      }

      // manage errors
      if (total !== 100) {
        this.totalPercentageForError = total;
        tagIdsAndPercentagesFormArray.controls.forEach((group: FormGroup) => {
          group.get('percentage').markAsTouched(); // untouched selects are not in red without this
          group.get('percentage').setErrors({ percentagesSum: true });
        });
        return { percentagesSum: true };
      }

      removeErrorOnSelects();
      return null;
    };
  }

  ngOnDestroy(): void {
    this.syncFormValidity();
    this.subscriptions?.unsubscribe();
  }
}
